// src/pages/EditTrip.tsx
import { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";
import DestinationSearchInput from "../components/DestinationSearchInput";

export default function EditTrip() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState<any>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [isCoOrganizer, setIsCoOrganizer] = useState(false);
  const [joinSelf, setJoinSelf] = useState(false);

  const [destinationCoords, setDestinationCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const { showConfirm } = useConfirmDialog();
  useEffect(() => {
    const fetchTrip = async () => {
      const { data, error } = await supabase
        .from("trips")
        .select("*")
        .eq("id", id!)
        .single();

      if (error || !data) {
        setError("Reise nicht gefunden");
        setLoading(false);
        return;
      }

      if (user?.id === data.creator_id) {
        setTrip(data);
        setDestinationCoords(
          data.destination_lat && data.destination_lng
            ? { lat: data.destination_lat, lng: data.destination_lng }
            : null
        );

        setIsCoOrganizer(false);
      } else {
        // 👥 Co-Organizer prüfen
        const { data: participant, error: participantError } = await supabase
          .from("trip_participants")
          .select("is_co_organizer")
          .eq("trip_id", data.id)
          .eq("user_id", user?.id ?? "")
          .single();

        if (participantError || !participant?.is_co_organizer) {
          setError("Nicht autorisiert");
        } else {
          setTrip(data);
          setDestinationCoords(
            data.destination_lat && data.destination_lng
              ? { lat: data.destination_lat, lng: data.destination_lng }
              : null
          );

          setIsCoOrganizer(true);
        }
      }

      // prüfe, ob der User bereits Teilnehmer ist
      const { data: existingParticipant } = await supabase
        .from("trip_participants")
        .select("user_id")
        .eq("trip_id", data.id)
        .eq("user_id", user!.id)
        .maybeSingle();

      setJoinSelf(!!existingParticipant);

      setLoading(false);
    };

    if (id && user) {
      fetchTrip();
    }
  }, [id, user]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" && (e.target as HTMLInputElement).checked;

    setTrip((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // ❗ Validierung nur 1x zentral
    if (new Date(trip.end_date) < new Date(trip.start_date)) {
      setError("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }

    let coverImageUrl = trip.cover_image_url;

    if (imageFile && user) {
      const fileExt = imageFile.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // 👉 1. altes Bild löschen, wenn vorhanden
      if (trip.cover_image_url) {
        // Extrahiere nur den Pfad nach dem Bucket-Namen
        const oldFilePath = trip.cover_image_url.split(
          "/storage/v1/object/public/trip-images/"
        )[1];

        if (oldFilePath) {
          const { error: deleteError } = await supabase.storage
            .from("trip-images")
            .remove([oldFilePath]);

          if (deleteError) {
            console.warn(
              "⚠️ Fehler beim Löschen des alten Bildes:",
              deleteError
            );
          } else {
          }
        }
      }

      // 👉 2. neues Bild hochladen
      const { error: uploadError } = await supabase.storage
        .from("trip-images")
        .upload(filePath, imageFile, {
          contentType: imageFile.type,
          cacheControl: "3600",
          upsert: true,
        });

      if (uploadError) {
        console.error("❌ Fehler beim Upload:", uploadError);
        setError("Fehler beim Hochladen des neuen Bildes");
        return;
      }

      // 👉 3. neue URL setzen
      coverImageUrl = supabase.storage
        .from("trip-images")
        .getPublicUrl(filePath).data.publicUrl;
    }

    const { error: updateError } = await supabase
      .from("trips")
      .update({
        name: trip.name,
        description: trip.description,
        destination: trip.destination,
        start_date: trip.start_date,
        end_date: trip.end_date,
        is_public: trip.is_public,
        requires_approval: trip.requires_approval,
        max_participants: trip.max_participants
          ? parseInt(trip.max_participants)
          : null,
        cover_image_url: coverImageUrl,
        destination_lat: destinationCoords?.lat ?? null,
        destination_lng: destinationCoords?.lng ?? null,
      })
      .eq("id", id!);

    if (updateError) {
      setError("Fehler beim Speichern der Änderungen");
    } else {
      // Teilnahme-Status aktualisieren
      const { data: currentEntry } = await supabase
        .from("trip_participants")
        .select("id")
        .eq("trip_id", id!)
        .eq("user_id", user!.id)
        .maybeSingle();

      if (joinSelf && !currentEntry) {
        await supabase.from("trip_participants").insert({
          trip_id: id!,
          user_id: user!.id,
        });
      } else if (!joinSelf && currentEntry) {
        await supabase
          .from("trip_participants")
          .delete()
          .eq("id", currentEntry.id);
      }

      // Jetzt weiterleiten
      navigate(`/trip/${id}`);
    }
  };

  const handleDeleteTrip = async () => {
    const confirmed = await showConfirm({
      title: "Reise löschen",
      message:
        "Möchtest du diese Reise wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
      icon: "warning",
    });

    if (!confirmed) return;

    // 1. Bild löschen (falls vorhanden)
    if (trip.cover_image_url) {
      const filePath = trip.cover_image_url.split(
        "/storage/v1/object/public/trip-images/"
      )[1];

      if (filePath) {
        const { error: deleteImageError } = await supabase.storage
          .from("trip-images")
          .remove([filePath]);

        if (deleteImageError) {
          console.warn(
            "⚠️ Bild konnte nicht gelöscht werden:",
            deleteImageError
          );
        } else {
        }
      }
    }

    // 2. Reise aus der DB löschen
    const { error: deleteTripError } = await supabase
      .from("trips")
      .delete()
      .eq("id", id!);

    if (deleteTripError) {
      setError("Fehler beim Löschen der Reise");
      console.error(deleteTripError);
      return;
    }

    // 3. Zurück zum Dashboard
    navigate("/dashboard");
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        Lade Daten...
      </div>
    );

  if (error && !trip)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-red-400">
        {error}
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-2xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6">
          <h2 className="text-2xl font-bold mb-6 text-slate-100">
            Reise bearbeiten
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Reisetitel */}
            <div>
              <label className="block mb-1 text-sm text-slate-400 font-medium">Reisetitel</label>
              <input
                type="text"
                name="name"
                value={trip.name}
                onChange={handleChange}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                required
              />
            </div>

            {/* Beschreibung */}
            <div>
              <label className="block mb-1 text-sm text-slate-400 font-medium">Beschreibung</label>
              <textarea
                name="description"
                value={trip.description}
                onChange={handleChange}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
              />
            </div>

            {/* Zielort */}
            <div>
              <label className="block mb-1 text-sm text-slate-400 font-medium">Zielort</label>
              <DestinationSearchInput
                value={trip.destination}
                onChange={(val) =>
                  setTrip((prev: any) => ({ ...prev, destination: val }))
                }
                onCoordinates={(lat, lng) => setDestinationCoords({ lat, lng })}
              />
            </div>
            {destinationCoords && (
              <div className="w-full h-64 mt-2 rounded-xl overflow-hidden border border-[#2a3347]">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/view?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&center=${destinationCoords.lat},${destinationCoords.lng}&zoom=12`}
                />
              </div>
            )}

            {/* Datum */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm text-slate-400 font-medium">Startdatum</label>
                <input
                  type="date"
                  name="start_date"
                  value={trip.start_date}
                  onChange={handleChange}
                  className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-slate-400 font-medium">Enddatum</label>
                <input
                  type="date"
                  name="end_date"
                  value={trip.end_date}
                  onChange={handleChange}
                  className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                  required
                />
              </div>
            </div>

            {/* Datum Validierung */}
            {new Date(trip.end_date) < new Date(trip.start_date) && (
              <p className="text-red-400 text-sm">
                Das Enddatum darf nicht vor dem Startdatum liegen.
              </p>
            )}

            {/* Teilnehmerzahl */}
            <div>
              <label className="block mb-1 text-sm text-slate-400 font-medium">
                Maximale Teilnehmer (optional)
              </label>
              <input
                type="number"
                name="max_participants"
                value={trip.max_participants || ""}
                onChange={handleChange}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                min={1}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={joinSelf}
                onChange={(e) => setJoinSelf(e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-slate-200 font-medium">Ich nehme selbst teil</label>
            </div>

            {/* Öffentlich */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_public"
                checked={trip.is_public}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <label className="text-slate-200 font-medium">Öffentlich sichtbar</label>
            </div>

            {/* Genehmigung notwendig */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="requires_approval"
                checked={trip.requires_approval}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <label className="text-slate-200 font-medium">
                Teilnahme muss vom Orga-Team genehmigt werden
              </label>
            </div>

            {/* Bild anzeigen & ersetzen */}
            {trip.cover_image_url && (
              <div className="mb-4">
                <label className="block mb-1 text-sm text-slate-400 font-medium">Aktuelles Bild</label>
                <img
                  src={trip.cover_image_url}
                  alt="Aktuelles Titelbild"
                  className="w-full h-48 object-cover rounded-xl mb-2"
                />
              </div>
            )}

            <div>
              <label className="block mb-1 text-sm text-slate-400 font-medium">
                Neues Titelbild (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="text-slate-300 text-sm"
              />
            </div>

            {/* Allgemeine Fehlermeldung */}
            {error && <p className="text-red-400 text-sm">{error}</p>}

            {/* Speichern */}
            <button
              type="submit"
              className="bg-sky-500 text-white py-2 px-4 rounded-lg hover:bg-sky-600 transition disabled:opacity-50"
              disabled={new Date(trip.end_date) < new Date(trip.start_date)}
            >
              Änderungen speichern
            </button>

            {/* Löschen-Button */}
            {trip.creator_id === user?.id && (
              <div className="mt-6 border-t border-[#2a3347] pt-4">
                <button
                  type="button"
                  onClick={handleDeleteTrip}
                  className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition"
                >
                  Reise löschen
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
